import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "../css/TermAddField.module.css";
import PlaceHolderTextField from "../../common/PlaceHolderTextField";
import Term, {
  definitionMaxSize,
  memoMaxSize,
  pronunciationMaxSize,
  termMaxSize,
} from "../Term";
import { MenuBookRounded } from "@mui/icons-material";
import IconButton from "../../../components/ui/IconButton";
import ActionMenu from "../../../components/ui/ActionMenu";
import { TermAddLineBreakMode } from "../../settings/util/termAddSettings";

interface TermAddFieldProps {
  term: Term;
  dictionaryDefinitions: string[];
  pronunciationHint?: string;
  showMemo: boolean;
  showPronunciation: boolean;
  lineBreakMode: TermAddLineBreakMode;
  onChangeTerm: (term: string) => void;
  onChangeDefinition: (definition: string) => void;
  onChangeMemo: (memo: string) => void;
  onChangePronunciation: (pronunciation: string) => void;
  onSelectDictionaryDefinition: (definition: string) => void;
  onSubmit: () => void;
  firstInputRef: (el: HTMLTextAreaElement | null) => void;
}

type FieldKey = "term" | "definition" | "memo" | "pronunciation";

export default function TermAddField(props: TermAddFieldProps) {
  const { t } = useTranslation();

  const [dictionaryMenuAnchor, setDictionaryMenuAnchor] =
    useState<HTMLElement | null>(null);

  const [onGlobalFocus, setOnGlobalFocus] = useState(false);
  const [wordOnFocus, setWordOnFocus] = useState(false);
  const [definitionOnFocus, setDefinitionOnFocus] = useState(false);
  const [memoOnFocus, setMemoOnFocus] = useState(false);
  const [pronunciationOnFocus, setPronunciationOnFocus] = useState(false);

  useEffect(() => {
    setOnGlobalFocus(
      wordOnFocus === true ||
        definitionOnFocus === true ||
        memoOnFocus === true ||
        pronunciationOnFocus === true,
    );
  }, [wordOnFocus, definitionOnFocus, memoOnFocus, pronunciationOnFocus]);

  const fieldRefs = useRef<Record<FieldKey, HTMLTextAreaElement | null>>({
    term: null,
    definition: null,
    memo: null,
    pronunciation: null,
  });

  const defaultHints = props.term.defaultHints;

  function setFieldRef(key: FieldKey) {
    return (el: HTMLTextAreaElement | null) => {
      fieldRefs.current[key] = el;
      if (key === "term") {
        props.firstInputRef(el);
      }
    };
  }

  // 모바일 앱과 같이 '다음 행으로 이동' 모드에서는 엔터키로 다음 칸으로 가고, 마지막 칸에서는 저장한다
  function handleKeyDown(
    event: React.KeyboardEvent<HTMLTextAreaElement>,
    key: FieldKey,
  ) {
    if (props.lineBreakMode !== TermAddLineBreakMode.nextRow) return;
    if (event.key !== "Enter" || event.shiftKey) return;
    // 한글 · 일본어 입력을 조합하는 중에 누른 엔터는 무시한다
    if (event.nativeEvent.isComposing) return;

    event.preventDefault();

    const visibleKeys: FieldKey[] = [
      "term",
      "definition",
      ...(props.showMemo ? (["memo"] as FieldKey[]) : []),
      ...(props.showPronunciation ? (["pronunciation"] as FieldKey[]) : []),
    ];
    const nextKey = visibleKeys[visibleKeys.indexOf(key) + 1];
    if (nextKey === undefined) {
      props.onSubmit();
      return;
    }
    fieldRefs.current[nextKey]?.focus();
  }

  return (
    <>
      <div className={styles.container}>
        <div className={styles.topContainer}>
          {props.dictionaryDefinitions.length > 0 && (
            <IconButton
              aria-label={t("OnevocaDictionary")}
              title={t("OnevocaDictionary")}
              icon={<MenuBookRounded />}
              size="sm"
              onClick={(e) => setDictionaryMenuAnchor(e.currentTarget)}
            />
          )}
        </div>
        <PlaceHolderTextField
          text={props.term.term}
          hint={t("Word")}
          minCount={1}
          maxCount={termMaxSize}
          lineStyle="multiple"
          handleTextChange={props.onChangeTerm}
          textAreaRef={setFieldRef("term")}
          onKeyDownTextArea={(e) => handleKeyDown(e, "term")}
          onChangeFocus={setWordOnFocus}
          onGlobalFocus={onGlobalFocus}
        />
        <PlaceHolderTextField
          text={props.term.definition}
          hint={
            defaultHints !== undefined && defaultHints.length !== 0
              ? defaultHints
              : t("Meaning")
          }
          minCount={0}
          maxCount={definitionMaxSize}
          lineStyle="multiple"
          handleTextChange={props.onChangeDefinition}
          textAreaRef={setFieldRef("definition")}
          onKeyDownTextArea={(e) => handleKeyDown(e, "definition")}
          onChangeFocus={setDefinitionOnFocus}
          onGlobalFocus={onGlobalFocus}
        />
        {props.showMemo && (
          <PlaceHolderTextField
            text={props.term.memo}
            hint={`${t("Description")} (${t("Optional")})`}
            maxCount={memoMaxSize}
            lineStyle="multiple"
            handleTextChange={props.onChangeMemo}
            textAreaRef={setFieldRef("memo")}
            onKeyDownTextArea={(e) => handleKeyDown(e, "memo")}
            onChangeFocus={setMemoOnFocus}
            onGlobalFocus={onGlobalFocus}
          />
        )}
        {props.showPronunciation && (
          <PlaceHolderTextField
            text={props.term.pronunciation}
            hint={
              props.pronunciationHint !== undefined &&
              props.pronunciationHint.length !== 0
                ? props.pronunciationHint
                : `${t("Pronunciation")} (${t("Optional")})`
            }
            maxCount={pronunciationMaxSize}
            lineStyle="multiple"
            handleTextChange={props.onChangePronunciation}
            textAreaRef={setFieldRef("pronunciation")}
            onKeyDownTextArea={(e) => handleKeyDown(e, "pronunciation")}
            onChangeFocus={setPronunciationOnFocus}
            onGlobalFocus={onGlobalFocus}
          />
        )}
      </div>
      <ActionMenu
        open={dictionaryMenuAnchor !== null}
        anchorEl={dictionaryMenuAnchor}
        title={t("OnevocaDictionary")}
        onClose={() => setDictionaryMenuAnchor(null)}
        items={props.dictionaryDefinitions.map((definition) => ({
          label: definition,
          onSelect: () => props.onSelectDictionaryDefinition(definition),
        }))}
      />
    </>
  );
}
